import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Users } from "lucide-react";
import { maskPhoneInput } from "@/lib/dateTimeMask";
import { toTitleCasePt } from "@/lib/format";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import LastUpdatedLabel from "@/components/LastUpdatedLabel";

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });

  const [procs, setProcs] = useState([]);
  const load = async () => {
    setItems((await http.get("/clients")).data);
    // Lote 91: contador de processos por cliente (falha silenciosa — a lista de clientes não depende disso)
    try { setProcs((await http.get("/processos")).data || []); } catch { /* ignora */ }
  };
  const procCount = useMemo(() => {
    const m = {};
    procs.forEach((p) => { if (p.client_id) m[p.client_id] = (m[p.client_id] || 0) + 1; });
    return m;
  }, [procs]);
  const totalProc = Object.values(procCount).reduce((a, b) => a + b, 0);
  useEffect(() => { load(); }, []);
  // Lote novo (02/10/2026, pedido do Robson): atualiza sozinho ao voltar pra tela e a
  // cada 15min enquanto fica aberta — mesmo hook já usado em Painel/Agenda/Calendário/
  // Para validar/Publicações/Processos.
  const { lastUpdated } = useAutoRefresh(load);

  const submit = async () => {
    try {
      // E-mail deixou de ser obrigatório (Lote 56, 30/09/2026) — manda null em vez de ""
      // quando vazio, porque o backend valida o formato de e-mail quando o campo vem
      // preenchido (EmailStr) e uma string vazia não passa nessa validação.
      await http.post("/clients", { ...form, email: form.email.trim() || null });
      toast.success("Cliente cadastrado");
      setForm({ name: "", phone: "", email: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cadastrar");
    }
  };

  const del = async (id) => {
    if (!confirm("Excluir este cliente?")) return;
    await http.delete(`/clients/${id}`);
    toast.info("Cliente excluído");
    load();
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto">
      <header className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] font-bold tracking-[0.28em] uppercase text-emerald-brand mb-2">Clientes</div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Cadastro de clientes</h1>
          {items.length > 0 && (
            <p className="text-stone-400 mt-1 text-xs font-medium" data-testid="clientes-resumo">
              {items.length} {items.length === 1 ? "cliente" : "clientes"} · {totalProc} {totalProc === 1 ? "processo" : "processos"}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-client-button" className="bg-stone-900 hover:bg-stone-800 text-white gap-2">
              <UserPlus className="w-4 h-4" strokeWidth={2} /> Novo cliente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">Novo cliente</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Nome completo</Label>
                <Input
                  data-testid="client-name-input"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Telefone (com DDD)</Label>
                <Input
                  data-testid="client-phone-input"
                  inputMode="numeric"
                  placeholder="(11) 99999-9999"
                  maxLength={16}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: maskPhoneInput(e.target.value) })}
                />
              </div>
              <div>
                <Label className="text-xs">E-mail (opcional)</Label>
                <Input
                  data-testid="client-email-input"
                  type="email"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button
                data-testid="submit-client-button"
                onClick={submit}
                disabled={!form.name || !form.phone}
                className="bg-emerald-brand text-white"
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
          <LastUpdatedLabel date={lastUpdated} />
        </div>
      </header>

      <div className="bg-white border border-stone-200 rounded-md">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
            <p className="text-stone-500 text-sm">Nenhum cliente cadastrado.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] uppercase tracking-widest">Nome</TableHead>
                <TableHead className="text-[11px] uppercase tracking-widest">Telefone</TableHead>
                <TableHead className="text-[11px] uppercase tracking-widest">E-mail</TableHead>
                <TableHead className="text-[11px] uppercase tracking-widest">Processos</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.client_id} data-testid={`client-row-${c.client_id}`}>
                  <TableCell className="font-medium">{toTitleCasePt(c.name)}</TableCell>
                  <TableCell className="text-stone-500 tabular-nums">{c.phone}</TableCell>
                  <TableCell className="text-stone-500">{c.email}</TableCell>
                  <TableCell>
                    {procCount[c.client_id] ? (
                      <Link to={`/processos?cliente=${c.client_id}`} data-testid={`client-procs-${c.client_id}`} className="text-xs font-semibold text-emerald-brand hover:underline tabular-nums">
                        {procCount[c.client_id]} {procCount[c.client_id] === 1 ? "processo" : "processos"}
                      </Link>
                    ) : (
                      <span className="text-xs text-stone-400" data-testid={`client-procs-${c.client_id}`}>nenhum</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      data-testid={`delete-client-${c.client_id}`}
                      variant="ghost" size="sm"
                      onClick={() => del(c.client_id)}
                      className="text-rose-brand hover:bg-rose-100 hover:text-rose-700"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
